import styled from "@emotion/styled";
import type { FC } from "react";
import translate from "src/lib/translate";
import { RefillColors } from "src/constants/refillColors";

const SIZE = 32;
// Radius chosen so the ring fills the box: RADIUS + STROKE/2 = SIZE/2.
const STROKE = 3;
const RADIUS = SIZE / 2 - STROKE / 2; // 14.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export type CountdownTone = "green" | "orange" | "red";

// Ring arc color per tone: green on track, orange running out, red once overdue (Figma
// Primary/criticalRed). The number itself is always white for contrast over the hero photo.
const RING_COLORS: Record<CountdownTone, string> = {
  green: RefillColors.inRange,
  orange: RefillColors.sunriseOrange,
  red: RefillColors.criticalRed
};

/** Translucent glass pill (Figma: 👑 Countdown). Sits over the hero photo. */
const Pill = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 0.375rem;
  padding: 1px 12px 1px 1px;
  border-radius: 120px;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 0.5px solid rgba(255, 255, 255, 0.25);
`;

const RingBox = styled.div`
  position: relative;
  width: ${SIZE}px;
  height: ${SIZE}px;
  flex-shrink: 0;
`;

const Ring = styled.svg`
  transform: rotate(-90deg);
`;

const Days = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-white);
`;

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-white);
  white-space: nowrap;
`;

/**
 * Countdown for the hub hero. `fraction` (0–1) fills the ring, `tone` picks green (on
 * track) vs orange (running out). When `overdue`, `days` is the days-past-due count and
 * the label flips to "days overdue" (the ring is empty — pass `fraction={0}`).
 */
const CountdownRing: FC<{
  days: number;
  fraction: number;
  tone?: CountdownTone;
  overdue?: boolean;
}> = ({ days, fraction, tone = "green", overdue = false }) => {
  const clamped = Math.min(1, Math.max(0, fraction));
  const dashArray = `${clamped * CIRCUMFERENCE} ${CIRCUMFERENCE}`;
  const color = RING_COLORS[tone];
  // `count` drives i18next pluralization (day/days); the number itself renders in the ring.
  const label = overdue
    ? translate("prepareRefillDialog.v2.countdown.daysOverdue", { count: days })
    : translate("prepareRefillDialog.v2.countdown.daysLeft", { count: days });

  return (
    <Pill>
      <RingBox>
        <Ring width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="rgba(255, 255, 255, 0.35)"
            strokeWidth={STROKE}
          />
          {clamped > 0 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={dashArray}
            />
          )}
        </Ring>
        <Days>{days}</Days>
      </RingBox>
      <Label>{label}</Label>
    </Pill>
  );
};

export default CountdownRing;
